import { useFlag } from '@innovamat/flags';
import type {
  ActivityParameters,
  AppletsInfo,
  MessageType,
  Tokens,
  UnityPayload,
  UserParameters,
} from '@innovamat/ga-features';
import {
  AppletResource,
  authAxiosInstance,
  WebAppletWrapper,
} from '@innovamat/ga-features';
import { getAcceptLanguage } from '@innovamat/localization';
import type { FullScreenHandle } from 'react-full-screen';
import { MissingFocusModal } from '../../../missing-focus-modal';
import { useMissingFocusModal } from '../../../missing-focus-modal/use-missing-focus-modal';

type UnityProps = {
  unityPayload: UnityPayload;
  unityActivity: any;
  onUpdateTokens: (tokens: Tokens) => void;
};

export type ActivityType = 'MOTOR' | 'QUIZ' | 'WEB' | 'ACTIVITY';

type Props = {
  type: ActivityType;
  unityProps?: UnityProps;
  onCloseApplet: () => void;
  onAppletIsCompleted: (
    args?: MessageType['arguments'],
    eventId?: MessageType['eventId']
  ) => void;
  onException: () => void;
  sceneName: string;
  packOrVariation: string;
  userParameters: UserParameters;
  appletInfo: AppletsInfo;
  activityParameters?: ActivityParameters;
  setEventData: (eventType: string, eventProperties?: any) => void;
  slackApiUrl: string;
  slackApiTokens: string;
  apolloServer: string;
  source?: 'weeklyPractice' | 'trainingZone' | 'catchUpZone';
  handleFS?: FullScreenHandle;
};

const SessionWrapper = ({
  type,
  unityProps,
  onCloseApplet,
  onAppletIsCompleted,
  onException,
  sceneName,
  packOrVariation,
  userParameters,
  appletInfo,
  activityParameters,
  setEventData,
  slackApiUrl,
  slackApiTokens,
  apolloServer,
  source,
  handleFS,
}: Props): JSX.Element => {
  const { unityPayload, unityActivity, onUpdateTokens } = unityProps || {};
  const { language, school, uid } = userParameters;
  const { regionParameters, region, solverUrl, webContentsUrl } = appletInfo;

  const { value: featureFlagEnabled } = useFlag(
    'showMissingFocusModal',
    false,
    { identifier: '', custom: { schoolId: userParameters.school || '' } }
  );

  const {
    showMissingFocusModal,
    blurCount,
    handleCloseMissingFocusModal,
    handleLayoutChange,
  } = useMissingFocusModal({
    courseOrder: userParameters.courseOrder,
    initialLayout: activityParameters?.layout,
    isEnabled: featureFlagEnabled,
    source,
    onExcessiveFocusLoss: onCloseApplet,
    handleFS,
  });

  const handleOnCloseApplet = (): void => {
    onCloseApplet();
  };

  const handleOnAppletIsCompleted = (
    args?: MessageType['arguments'],
    eventId?: MessageType['eventId']
  ): void => {
    onAppletIsCompleted(args, eventId);
  };

  // End Applet health monitoring

  // ENABLE messages for secondary on 'secondary-web-errors' slack channel
  appletInfo.slackApiUrl = slackApiUrl;
  appletInfo.slackApiTokens = slackApiTokens;

  const appletWrapperLanguage = region
    ? getAcceptLanguage(region, language)
    : language;

  return (
    <>
      <MissingFocusModal
        blurCount={blurCount}
        isOpen={showMissingFocusModal}
        onClose={handleCloseMissingFocusModal}
      />

      {type === 'WEB' ? (
        <WebAppletWrapper
          userParameters={{
            ...userParameters,
            language: appletWrapperLanguage,
          }}
          regionalParameters={regionParameters}
          activityParameters={activityParameters!}
          onCloseApplet={onCloseApplet}
          onAppletIsCompleted={handleOnAppletIsCompleted}
          webContentsUrl={webContentsUrl!}
          solverUrl={solverUrl!}
          setEventData={setEventData}
          onLayoutChange={handleLayoutChange}
        />
      ) : (
        <AppletResource
          apolloServer={apolloServer}
          axiosInstance={authAxiosInstance}
          setEventData={setEventData}
          payload={unityPayload}
          unityActivity={unityActivity}
          onCloseApp={handleOnCloseApplet}
          onAppletIsCompleted={onAppletIsCompleted}
          sceneName={sceneName}
          packOrVariation={packOrVariation}
          language={language}
          appletInfo={appletInfo}
          style={{ maxWidth: '100% !important' }}
          onUpdateTokens={onUpdateTokens}
          school={school}
          uid={uid}
          onException={onException}
        />
      )}
    </>
  );
};

export default SessionWrapper;
